/**
 * Inline Element AST Nodes
 *
 * Inline elements appear WITHIN lines (in-flow with text):
 * - PlainText: Regular text
 * - HighlightedText: Text with optional highlighting
 * - InlineInput: Small input widget within a line
 */

import { z } from 'zod';
import { AST_TYPES, INPUT_TYPES } from '../constants';

function parseOrThrow<T>(schema: z.ZodType<T>, data: unknown, label: string): T {
  const result = schema.safeParse(data);
  if (!result.success) {
    const issue = result.error.issues[0];
    if (!issue) throw new Error(`${label}: validation failed`);
    const path = issue.path.length > 0 ? issue.path.join('.') : '<root>';
    throw new Error(`${label}: ${path} ${issue.message}`);
  }
  return result.data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Visual Fraction Shape Constants
// ═══════════════════════════════════════════════════════════════════════════

export const VISUAL_FRACTION_SHAPES = ['bar', 'circle', 'triangle', 'grid'] as const;
export type VisualFractionShape = (typeof VISUAL_FRACTION_SHAPES)[number];

/** Named enum for visual fraction shapes — use instead of raw string literals. */
export const VISUAL_FRACTION_SHAPE = {
  BAR: 'bar',
  CIRCLE: 'circle',
  TRIANGLE: 'triangle',
  GRID: 'grid',
} as const satisfies Record<string, VisualFractionShape>;

/** Default shape size in rem — used as SVG viewport for circle/triangle, and as part-cell size for bar/grid. */
export const VISUAL_FRACTION_DEFAULT_SIZE = 1.35;

/**
 * Maximum number of divisions allowed for any VisualFraction shape.
 * Enforced by both the AST schema (DSL parse) and the JSON schema (external data).
 * Edit this single value to raise or lower the limit across the entire pipeline.
 */
export const VISUAL_FRACTION_MAX_DIVISIONS = 100;

// ═══════════════════════════════════════════════════════════════════════════
// Free Text Inline (Natural writing - no wrapper needed!)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Free text inline schema - plain text/numbers WITHOUT special formatting
 *
 * Use this for normal text. Only use NUMBER_TEXT when you need highlighting!
 *
 * @example
 * { type: "FreeText", value: "25 + " }
 * { type: "FreeText", value: " = 30" }
 */
export const FreeTextInlineSchema = z.object({
  type: z.literal(AST_TYPES.FREE_TEXT),
  value: z.string(),
});

export type FreeTextInline = z.infer<typeof FreeTextInlineSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Space Inline
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Space inline schema - explicit spacing element
 *
 * Represents one or more consecutive spaces as a single space element.
 * This allows for deterministic and explicit control over spacing in the layout.
 *
 * @example
 * { type: "Space" }
 */
export const SpaceInlineSchema = z.object({
  type: z.literal(AST_TYPES.SPACE),
});

export type SpaceInline = z.infer<typeof SpaceInlineSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Highlighted Text Inline
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Highlighted text inline schema - text with optional highlighting
 *
 * highlight: [start, end] character positions for visual emphasis
 *
 * @example
 * {
 *   type: "HighlightedText",
 *   value: "151 000",
 *   highlight: [0, 3]  // Highlights "151"
 * }
 */
export const HighlightedTextInlineSchema = z.object({
  type: z.literal(AST_TYPES.HIGHLIGHTED_TEXT),
  value: z.string(),
  highlight: z.tuple([z.number(), z.number()]).optional(),
});

export type HighlightedTextInline = z.infer<typeof HighlightedTextInlineSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Color Text Inline
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Color text inline schema - text with color and optional range coloring
 *
 * highlight: [start, end] character positions for colored range
 * color: CSS color value (e.g., "red", "#ff0000")
 *
 * @example
 * {
 *   type: "ColorText",
 *   value: "123456",
 *   color: "red",
 *   highlight: [0, 3]  // Colors "123" in red
 * }
 */
export const ColorTextInlineSchema = z.object({
  type: z.literal(AST_TYPES.COLOR_TEXT),
  value: z.string(),
  color: z.string(),
  highlight: z.tuple([z.number(), z.number()]).optional(),
});

export type ColorTextInline = z.infer<typeof ColorTextInlineSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Bold Text Inline
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Bold text inline schema - text with optional range bolding
 *
 * highlight: [start, end] character positions for bold range
 *
 * @example
 * {
 *   type: "BoldText",
 *   value: "123456",
 *   highlight: [0, 3]  // Bolds "123"
 * }
 */
export const BoldTextInlineSchema = z.object({
  type: z.literal(AST_TYPES.BOLD_TEXT),
  value: z.string(),
  highlight: z.tuple([z.number(), z.number()]).optional(),
});

export type BoldTextInline = z.infer<typeof BoldTextInlineSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Inline Input Widget
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Inline input widget schema - small input that appears within a line
 *
 * This is for SHORT inputs (1-5 characters) that flow with text
 * For longer inputs, consider a standalone tool
 *
 * @example String input (W_S_I):
 * {
 *   type: "InlineInput",
 *   name: "x",
 *   inputType: "string",
 *   length: 5,        // Visual width in characters
 *   answer: "hello"
 * }
 *
 * @example Numeric input (W_N_I):
 * {
 *   type: "InlineInput",
 *   name: "x",
 *   inputType: "numeric",
 *   digits: 2,        // Maximum number of digits
 *   answer: 42
 * }
 */
export const InlineInputWidgetSchema = z.discriminatedUnion('inputType', [
  z.object({
    type: z.literal(AST_TYPES.INLINE_INPUT),
    name: z.string(),
    inputType: z.literal(INPUT_TYPES.STRING),
    length: z.number().int().positive(),
    placeholder: z.string().max(32).optional(),
    answer: z.union([z.string(), z.number()]).optional(),
  }),
  z.object({
    type: z.literal(AST_TYPES.INLINE_INPUT),
    name: z.string(),
    inputType: z.literal(INPUT_TYPES.NUMERIC),
    digits: z.number().int().positive(),
    placeholder: z.string().max(32).optional(),
    answer: z.union([z.string(), z.number()]).optional(),
  }),
]);

export type InlineInputWidget = z.infer<typeof InlineInputWidgetSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Fraction Inline Widget
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Fraction inline widget schema
 *
 * Modes determined by presence of numerator/denominator:
 * - both provided → readonly display
 * - only numerator → denominator input
 * - only denominator → numerator input
 * - neither → both are inputs
 *
 * @example Readonly: { type: "Fraction", name: "x", numerator: 5, denominator: 3 }
 * @example Denominator input: { type: "Fraction", name: "y", numerator: 5, answer: "5/3" }
 * @example Both inputs: { type: "Fraction", name: "z", answer: "5/3" }
 */
export const FractionInlineSchema = z.object({
  type: z.literal(AST_TYPES.FRACTION),
  name: z.string(),
  numerator: z.number().optional(),
  denominator: z.number().optional(),
  answer: z.string().optional(),
  simplified: z.boolean().default(true),
  placeholder: z.string().max(32).optional(),
});

export type FractionInline = z.infer<typeof FractionInlineSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Image Inline
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Image inline schema
 *
 * @example
 * {
 *   type: "Image",
 *   name: "fig1",
 *   src: "https://example.com/fig1.png",
 *   alt: "A cat",
 *   width: 120,
 *   height: 80
 * }
 */
const IMAGE_SRC_SCHEME_RE = /^(https?:\/\/|data:image\/)/i;

export const ImageInlineSchema = z.object({
  type: z.literal(AST_TYPES.IMAGE),
  name: z.string().min(1),
  src: z
    .string()
    .url()
    .refine((s) => IMAGE_SRC_SCHEME_RE.test(s), {
      message: 'src must use http(s) or data:image scheme',
    }),
  alt: z.string(),
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
});

export type ImageInline = z.infer<typeof ImageInlineSchema>;
// Visual Fraction Inline
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Visual Fraction inline widget schema.
 *
 * @example
 * VISUAL_FRACTION[name="vf1", shape="bar", divisions=8, filled=[3], answer=[2], label="3/8"]
 */
export const VisualFractionInlineSchema = z
  .object({
    type: z.literal(AST_TYPES.VISUAL_FRACTION),
    name: z.string(),
    shape: z.enum(VISUAL_FRACTION_SHAPES).default('bar'),
    divisions: z
      .number()
      .int()
      .positive()
      .max(VISUAL_FRACTION_MAX_DIVISIONS, {
        message: `divisions cannot exceed ${VISUAL_FRACTION_MAX_DIVISIONS}`,
      }),
    filled: z.array(z.number().int().nonnegative()).default([]),
    answer: z.array(z.number().int().nonnegative()).default([]),
    orientation: z.enum(['horizontal', 'vertical']).optional(),
    label: z.string().optional(),
    /** Unified author size.
     * - bar / grid:         overrides `--visual-fraction-part-size` (cell size).
     * - circle / triangle:  maps to `--visual-fraction-size` with the renderer scale factor.
     * Omit to use CSS defaults. */
    size: z.number().positive().optional(),
  })
  .superRefine(validateVisualFractionCapacity);
export type VisualFractionInline = z.infer<typeof VisualFractionInlineSchema>;

export function validateVisualFractionCapacity(
  data: {
    divisions: number;
    filled: number[];
    answer: number[];
    shape?: VisualFractionShape;
  },
  ctx: z.RefinementCtx
): void {
  const filledTotal = data.filled.reduce((sum, count) => sum + count, 0);
  const answerTotal = data.answer.reduce((sum, count) => sum + count, 0);

  if (filledTotal > data.divisions) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `filled total (${filledTotal}) cannot exceed divisions (${data.divisions})`,
      path: ['filled'],
    });
  }

  if (filledTotal + answerTotal > data.divisions) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `filled + answer total (${filledTotal + answerTotal}) cannot exceed divisions (${data.divisions})`,
      path: ['answer'],
    });
  }

  const shape = data.shape;

  if (shape === 'triangle' && !isPerfectSquare(data.divisions)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `triangle divisions (${data.divisions}) must be a perfect square to render equal triangle parts`,
      path: ['divisions'],
    });
  }

  if (shape === 'grid' && !isPerfectSquare(data.divisions)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `grid divisions (${data.divisions}) must be a perfect square to render equal rows and columns`,
      path: ['divisions'],
    });
  }
}

function isPerfectSquare(value: number): boolean {
  const root = Math.sqrt(value);
  return Number.isInteger(root);
}

// ═══════════════════════════════════════════════════════════════════════════
// Inline Drag Option
// ═══════════════════════════════════════════════════════════════════════════

/**
 * A single draggable option rendered inline within the text flow.
 *
 * Unlike DragOptionsTool (which groups all options in a fixed container),
 * this element is placed directly in the line alongside other text elements.
 *
 * @example
 * INLINE_DRAG_OPTION["94"]  → { type: "InlineDragOption", value: "94" }
 */
export const InlineDragOptionSchema = z.object({
  type: z.literal(AST_TYPES.INLINE_DRAG_OPTION),
  value: z.string().min(1),
});

export type InlineDragOption = z.infer<typeof InlineDragOptionSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Clock Inline Widget
// ═══════════════════════════════════════════════════════════════════════════

export const CLOCK_TYPES = ['analog', 'digital'] as const;
export const CLOCK_MODES = ['display', 'interactive'] as const;
export const CLOCK_FORMATS = ['12h', '24h'] as const;

export type ClockType = typeof CLOCK_TYPES[number];
export type ClockMode = typeof CLOCK_MODES[number];
export type ClockFormat = typeof CLOCK_FORMATS[number];

// 24h internal format: 00-23 hours, 00-59 minutes
export const CLOCK_TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;
const TimeStringSchema = z.string().regex(CLOCK_TIME_REGEX, 'must be "HH:MM" in 24h format');

const DisplayClockInlineSchema = z.object({
  type: z.literal(AST_TYPES.CLOCK),
  mode: z.literal('display'),
  clockType: z.enum(CLOCK_TYPES),
  value: TimeStringSchema,
  format: z.enum(CLOCK_FORMATS).optional(),
  name: z.string().optional(),
});

const InteractiveClockInlineSchema = z.object({
  type: z.literal(AST_TYPES.CLOCK),
  mode: z.literal('interactive'),
  clockType: z.literal('analog'),
  value: TimeStringSchema,
  answer: TimeStringSchema,
  format: z.enum(CLOCK_FORMATS).optional(),
  snap: z.number().int().min(1).max(60).optional(),
  name: z.string(),
});

export const ClockInlineSchema = z.discriminatedUnion('mode', [
  DisplayClockInlineSchema,
  InteractiveClockInlineSchema,
]);

export type ClockInline = z.infer<typeof ClockInlineSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Union Type
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Union of all inline element types
 */
export const InlineNodeSchema = z.union([
  FreeTextInlineSchema,
  SpaceInlineSchema,
  HighlightedTextInlineSchema,
  ColorTextInlineSchema,
  BoldTextInlineSchema,
  InlineInputWidgetSchema,
  FractionInlineSchema,
  ImageInlineSchema,
  VisualFractionInlineSchema,
  InlineDragOptionSchema,
  ClockInlineSchema,
]);

export type InlineNode = z.infer<typeof InlineNodeSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Factory Functions
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Create a free text inline element
 */
export function createFreeText(value: string): FreeTextInline {
  return FreeTextInlineSchema.parse({
    type: AST_TYPES.FREE_TEXT,
    value,
  });
}

/**
 * Create a space inline element
 */
export function createSpace(): SpaceInline {
  return SpaceInlineSchema.parse({
    type: AST_TYPES.SPACE,
  });
}

/**
 * Create a highlighted text inline element
 */
export function createHighlightedText(
  value: string,
  highlight?: [number, number]
): HighlightedTextInline {
  return HighlightedTextInlineSchema.parse({
    type: AST_TYPES.HIGHLIGHTED_TEXT,
    value,
    highlight,
  });
}

/**
 * Create a color text inline element
 */
export function createColorText(
  value: string,
  color: string,
  highlight?: [number, number]
): ColorTextInline {
  return ColorTextInlineSchema.parse({
    type: AST_TYPES.COLOR_TEXT,
    value,
    color,
    highlight,
  });
}

/**
 * Create a bold text inline element
 */
export function createBoldText(value: string, highlight?: [number, number]): BoldTextInline {
  return BoldTextInlineSchema.parse({
    type: AST_TYPES.BOLD_TEXT,
    value,
    highlight,
  });
}

/**
 * Create a string inline input widget (W_S_I)
 */
export function createStringInput(
  name: string,
  length: number,
  answer?: string | number,
  placeholder?: string
): InlineInputWidget {
  return parseOrThrow(
    InlineInputWidgetSchema,
    {
      type: AST_TYPES.INLINE_INPUT,
      name,
      inputType: INPUT_TYPES.STRING,
      length,
      placeholder,
      answer,
    },
    'STRING_INPUT'
  );
}

/**
 * Create a numeric inline input widget (W_N_I)
 */
export function createNumericInput(
  name: string,
  digits: number,
  answer?: string | number,
  placeholder?: string
): InlineInputWidget {
  return parseOrThrow(
    InlineInputWidgetSchema,
    {
      type: AST_TYPES.INLINE_INPUT,
      name,
      inputType: INPUT_TYPES.NUMERIC,
      digits,
      placeholder,
      answer,
    },
    'NUMERIC_INPUT'
  );
}

/**
 * Create an image inline element
 */
export function createImage(
  name: string,
  src: string,
  alt: string,
  options: { width?: number; height?: number } = {}
): ImageInline {
  return ImageInlineSchema.parse({
    type: AST_TYPES.IMAGE,
    name,
    src,
    alt,
    ...options,
  });
}

/**
 * Create a fraction inline widget
 */
export function createFraction(
  name: string,
  options: {
    numerator?: number;
    denominator?: number;
    answer?: string;
    simplified?: boolean;
    placeholder?: string;
  } = {}
): FractionInline {
  return FractionInlineSchema.parse({
    type: AST_TYPES.FRACTION,
    name,
    ...options,
  });
}

/**
 * Create a visual fraction inline widget
 */
export function createVisualFraction(
  name: string,
  divisions: number,
  options: {
    shape?: VisualFractionShape;
    filled?: number[];
    answer?: number[];
    orientation?: 'horizontal' | 'vertical';
    label?: string;
    /** Unified author size. */
    size?: number;
  } = {}
): VisualFractionInline {
  return VisualFractionInlineSchema.parse({
    type: AST_TYPES.VISUAL_FRACTION,
    name,
    divisions,
    ...options,
  });
}

/**
 * Create a single inline draggable option
 */
export function createInlineDragOption(value: string): InlineDragOption {
  return InlineDragOptionSchema.parse({
    type: AST_TYPES.INLINE_DRAG_OPTION,
    value,
  });
}

/**
 * Create a display-mode clock inline widget
 */
export function createDisplayClock(params: {
  clockType: ClockType;
  value: string;
  format?: ClockFormat;
  name?: string;
}): ClockInline {
  return ClockInlineSchema.parse({
    type: AST_TYPES.CLOCK,
    mode: 'display',
    ...params,
  });
}

/**
 * Create an interactive-mode clock inline widget (analog only)
 */
export function createInteractiveClock(params: {
  name: string;
  value: string;
  answer: string;
  format?: ClockFormat;
  snap?: number;
}): ClockInline {
  return ClockInlineSchema.parse({
    type: AST_TYPES.CLOCK,
    mode: 'interactive',
    clockType: 'analog',
    ...params,
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Type Guards
// ═══════════════════════════════════════════════════════════════════════════

export function isFreeText(value: unknown): value is FreeTextInline {
  return FreeTextInlineSchema.safeParse(value).success;
}

export function isSpace(value: unknown): value is SpaceInline {
  return SpaceInlineSchema.safeParse(value).success;
}

export function isHighlightedText(value: unknown): value is HighlightedTextInline {
  return HighlightedTextInlineSchema.safeParse(value).success;
}

export function isColorText(value: unknown): value is ColorTextInline {
  return ColorTextInlineSchema.safeParse(value).success;
}

export function isBoldText(value: unknown): value is BoldTextInline {
  return BoldTextInlineSchema.safeParse(value).success;
}

export function isInlineInput(value: unknown): value is InlineInputWidget {
  return InlineInputWidgetSchema.safeParse(value).success;
}

export function isFraction(value: unknown): value is FractionInline {
  return FractionInlineSchema.safeParse(value).success;
}

export function isImage(value: unknown): value is ImageInline {
  return ImageInlineSchema.safeParse(value).success;
}

export function isClock(value: unknown): value is ClockInline {
  return ClockInlineSchema.safeParse(value).success;
}

export function isInlineNode(value: unknown): value is InlineNode {
  return InlineNodeSchema.safeParse(value).success;
}

export function isVisualFractionInline(value: unknown): value is VisualFractionInline {
  return VisualFractionInlineSchema.safeParse(value).success;
}

export function isInlineDragOption(value: unknown): value is InlineDragOption {
  return InlineDragOptionSchema.safeParse(value).success;
}
